// User Profile Page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchCrmEntities, fetchTenant } from '../../lib/queryFunctions';
import SidebarLayout from '../../components/layout/SidebarLayout';
import PushNotificationSettings from '../../components/notifications/PushNotificationSettings';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2, Save, User, Lock, Upload, Camera, LifeBuoy, RotateCcw, RotateCw, Sparkles, Bell, Search, ChevronsUpDown, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { fmtDatetime } from '../../utils/dateUtils';
import { TIMEZONES } from '../../utils/timezoneConstants';
import api from '../../services/api';

// ── Onboarding Re-launch card ─────────────────────────────────────────────────
const OnboardingRelaunch = () => {
  const { completeItem, checklist, doneCount, allDone } = useOnboarding();
  const [resetting, setResetting] = React.useState(false);

  const handleReset = async () => {
    setResetting(true);
    try {
      await api.post('/onboarding/reset');
      // Clear the dismissed flag so the checklist reappears
      Object.keys(localStorage).forEach(k => {
        if (k.startsWith('onboarding_dismissed_')) localStorage.removeItem(k);
      });
      window.location.reload();
    } catch {
      toast.error('Failed to re-launch tour');
      setResetting(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-sm">Getting Started Tour</p>
              <p className="text-xs text-muted-foreground mt-1">
                {allDone
                  ? 'You completed all onboarding steps. Reset to re-launch the guided tour.'
                  : `${doneCount} of ${checklist.length} steps completed. Re-launch to start over.`}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={resetting}
            className="shrink-0"
            data-testid="relaunch-onboarding-btn"
          >
            {resetting
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resetting...</>
              : <><RotateCcw className="w-4 h-4 mr-2" />Re-launch Tour</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


const UserProfilePage = () => {
  const { user: authUser } = useAuth();
  const { completeItem } = useOnboarding();
  const location = useLocation();
  const tabFromUrl = new URLSearchParams(location.search).get('tab') || 'profile';
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [rotatingPhoto, setRotatingPhoto] = useState(false);

  const handleRotateProfilePhoto = async () => {
    if (!photoBlob || rotatingPhoto) return;
    setRotatingPhoto(true);
    try {
      const blob = await new Promise((resolve, reject) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalHeight;
          canvas.height = img.naturalWidth;
          const ctx = canvas.getContext('2d');
          ctx.translate(img.naturalHeight / 2, img.naturalWidth / 2);
          ctx.rotate(Math.PI / 2);
          ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
          canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas toBlob failed')), 'image/jpeg', 0.92);
        };
        img.onerror = reject;
        img.src = photoBlob;
      });
      const rotatedFile = new File([blob], 'profile-rotated.jpg', { type: 'image/jpeg' });
      await handlePhotoUpload(rotatedFile);
    } catch {
      toast.error('Failed to rotate photo');
    } finally {
      setRotatingPhoto(false);
    }
  };
  const [tzOpen, setTzOpen] = useState(false);
  const [photoBlob, setPhotoBlob] = useState(null);
  const [alertPrefsOpen, setAlertPrefsOpen] = useState(false);
  const [subscribedCount, setSubscribedCount] = useState(0);
  const [alertPrefs, setAlertPrefs] = useState(null);
  const [alertAccounts, setAlertAccounts] = useState([]);
  const [alertAccountSearch, setAlertAccountSearch] = useState('');
  const [alertTab, setAlertTab] = useState('types');
  // isFarm from query (replaces old useState + useEffect fetch)

  const ALERT_TYPES = [
    { key: 'repeat_view',                     label: 'Repeat View',         desc: 'When a horse gets a repeat viewing' },
    { key: 'vet_procedure_all',               label: 'All Vet Procedures',  desc: 'Any vetting procedure is scheduled or completed' },
    { key: 'vet_procedure_heart_scan',        label: 'Heart Scan',          desc: 'Heart scan scheduled or completed' },
    { key: 'vet_procedure_measurement',       label: 'Measurement',         desc: 'Measurement scheduled or completed' },
    { key: 'vet_procedure_physical',          label: 'Physical',            desc: 'Physical examination scheduled or completed' },
    { key: 'vet_procedure_scope_in_person',   label: 'Scope - In Person',   desc: 'In-person scope scheduled or completed' },
    { key: 'vet_procedure_scope_repository',  label: 'Scope - Repository',  desc: 'Repository scope scheduled or completed' },
    { key: 'vet_procedure_ultrasound',        label: 'Ultrasound',          desc: 'Ultrasound scheduled or completed' },
    { key: 'vet_procedure_xray_in_person',    label: 'X-ray - In Person',   desc: 'In-person X-ray scheduled or completed' },
    { key: 'vet_procedure_xray_repository',   label: 'X-ray - Repository',  desc: 'Repository X-ray scheduled or completed' },
    { key: 'vet_procedure_other',             label: 'Other Procedure',     desc: 'Any other vetting procedure' },
    { key: 'sale_result',                     label: 'Sale Result',         desc: 'When a horse is sold, RNA, or withdrawn' },
  ];

  const loadAlertPrefs = async () => {
    try {
      const [prefsRes, acctRes] = await Promise.all([
        api.get('/tenant-alert-prefs'),
        api.get('/crm-entities?crm_class=account'),
      ]);
      setAlertPrefs(prefsRes.data);
      setAlertAccounts(acctRes.data || []);
      setSubscribedCount((prefsRes.data.subscribed_account_ids || []).length);
    } catch { /* ignore */ }
  };


  const toggleAlertType = (key, field) => {
    const updated = {
      ...alertPrefs,
      alert_preferences: {
        ...alertPrefs.alert_preferences,
        [key]: { ...alertPrefs.alert_preferences[key], [field]: !alertPrefs.alert_preferences[key]?.[field] },
      },
    };
    setAlertPrefs(updated);
    api.put('/tenant-alert-prefs', { alert_preferences: updated.alert_preferences, subscribed_account_ids: updated.subscribed_account_ids }).catch(() => toast.error('Failed to save'));
  };

  const toggleAlertMethod = (key, method) => {
    const current = alertPrefs.alert_preferences[key]?.methods || [];
    const updated = {
      ...alertPrefs,
      alert_preferences: {
        ...alertPrefs.alert_preferences,
        [key]: { ...alertPrefs.alert_preferences[key], methods: current.includes(method) ? current.filter(m => m !== method) : [...current, method] },
      },
    };
    setAlertPrefs(updated);
    api.put('/tenant-alert-prefs', { alert_preferences: updated.alert_preferences, subscribed_account_ids: updated.subscribed_account_ids }).catch(() => toast.error('Failed to save'));
  };

  const toggleAlertAccount = (id) => {
    const current = new Set(alertPrefs.subscribed_account_ids || []);
    current.has(id) ? current.delete(id) : current.add(id);
    const updated = { ...alertPrefs, subscribed_account_ids: [...current] };
    setAlertPrefs(updated);
    setSubscribedCount(updated.subscribed_account_ids.length);
    api.put('/tenant-alert-prefs', { alert_preferences: updated.alert_preferences, subscribed_account_ids: updated.subscribed_account_ids }).catch(() => toast.error('Failed to save'));
  };
  const [user, setUser] = useState(null);
  // ticketsLoading now comes from useQuery (ticketsLoading2 alias)
  const qc = useQueryClient();

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: QK.userProfile(),
    queryFn: async () => {
      const response = await api.get('/profile/me');
      const data = response.data;
      // Profile photo stays as blob for rotation support
      let photoBlob = null;
      if (data.profile_photo_url) {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(data.profile_photo_url, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) photoBlob = URL.createObjectURL(await res.blob());
        } catch { /* ignore */ }
      }
      return { ...data, _photoBlob: photoBlob };
    },
  });
  useEffect(() => {
    if (profileData) {
      setUser(profileData);
      setProfileForm({
        first_name: profileData.first_name || '',
        last_name: profileData.last_name || '',
        phone: profileData.phone || '',
        profile_photo_url: profileData.profile_photo_url || '',
        timezone: profileData.timezone || 'America/New_York',
        language: profileData.language || 'en',
        office_location: profileData.office_location || '',
      });
      if (profileData._photoBlob) setPhotoBlob(profileData._photoBlob);
    }
  }, [profileData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: myTickets = [], isLoading: ticketsLoading2 } = useQuery({
    queryKey: QK.myTickets(),
    queryFn: () => api.get('/chatbot/my-tickets').then(r => r.data),
  });

  const { data: tenant } = useQuery({
    queryKey: QK.tenant(authUser?.tenant_id),
    queryFn: fetchTenant(authUser?.tenant_id),
    enabled: !!authUser?.tenant_id,
  });
  const isFarmFromQuery = tenant?.is_farm === true;

  const loading = profileLoading;

  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    profile_photo_url: '',
    timezone: 'America/New_York',
    language: 'en',
    office_location: ''
  });
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (tabFromUrl === 'alerts') loadAlertPrefs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // isFarm: use query data (isFarm state was removed in Phase 2 migration)
  const isFarmComputed = isFarmFromQuery ?? false;

  const invalidateProfile = () => qc.invalidateQueries({ queryKey: QK.userProfile() });

  const handlePhotoUpload = async (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const previewUrl = URL.createObjectURL(file);
      setPhotoBlob(previewUrl);

      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'profile_photo');

      const response = await fetch(
        `${window.location.origin}/api/files/upload`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      const fileUrl = `${window.location.origin}/api/files/download/${data.id}`;

      // Auto-save photo
      await api.put('/profile/me', {
        profile_photo_url: fileUrl
      });

      setProfileForm(prev => ({...prev, profile_photo_url: fileUrl}));
      // Re-fetch blob from server to confirm upload
      try {
        const token2 = localStorage.getItem('token');
        const blobRes = await fetch(fileUrl, { headers: { Authorization: `Bearer ${token2}` } });
        if (blobRes.ok) setPhotoBlob(URL.createObjectURL(await blobRes.blob()));
      } catch { /* keep preview URL on failure */ }
      
      toast.success('Profile photo updated');
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error('Failed to upload photo');
      setPhotoBlob(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileForm.first_name || !profileForm.last_name) {
      toast.error('First and last name are required');
      return;
    }

    setSaving(true);
    try {
      await api.put('/profile/me', profileForm);
      toast.success('Profile updated successfully');
      invalidateProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSaving(true);
    try {
      await api.post('/profile/me/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully');
      completeItem('update_password');
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      'system_owner': 'System Owner',
      'tenant_system_admin': 'System Admin',
      'tenant_acct_manager': 'Account Manager',
      'tenant_acct_admin': 'Account Admin',
      'tenant_team_leader': 'Team Leader',
      'tenant_card_runner': 'Card Runner',
      'tenant_sale_clerk': 'Sale Clerk',
      'tenant_farm_manager': 'Farm Manager',
      'tenant_other': 'Other',
      'owner': 'Owner'
    };
    return roleLabels[role] || role;
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-serif text-3xl font-semibold">My Profile</h1>
          <p className="text-muted-foreground mt-1">Manage your personal information and settings</p>
        </div>

        <Tabs defaultValue={tabFromUrl} onValueChange={v => { if (v === 'alerts' && !alertPrefs) loadAlertPrefs(); }}>
          <TabsList>
            <TabsTrigger value="profile">
              <User className="w-4 h-4 mr-2" /> Profile Details
            </TabsTrigger>
            <TabsTrigger value="security">
              <Lock className="w-4 h-4 mr-2" /> Security
            </TabsTrigger>
            <TabsTrigger value="alerts">
              <Bell className="w-4 h-4 mr-2" /> Notifications
            </TabsTrigger>
            <TabsTrigger value="support">
              <LifeBuoy className="w-4 h-4 mr-2" /> Support Tickets
            </TabsTrigger>
          </TabsList>

          {/* Profile Details Tab */}
          <TabsContent value="profile" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Profile Photo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-6">
                  <div className="w-32 h-32 bg-muted rounded-full flex items-center justify-center border-2 border-border shrink-0 overflow-hidden">
                    {photoBlob ? (
                      <img src={photoBlob} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-16 h-16 text-muted-foreground" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Button
                      onClick={() => document.getElementById('photo-upload-input').click()}
                      disabled={uploadingPhoto || rotatingPhoto}
                      variant="outline"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                    </Button>
                    {photoBlob && (
                      <Button
                        onClick={handleRotateProfilePhoto}
                        disabled={rotatingPhoto || uploadingPhoto}
                        variant="outline"
                        data-testid="rotate-profile-photo-btn"
                      >
                        <RotateCw className={`w-4 h-4 mr-2 ${rotatingPhoto ? 'animate-spin' : ''}`} />
                        {rotatingPhoto ? 'Rotating...' : 'Rotate'}
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">
                      JPG, PNG or GIF. Max 5MB.
                    </p>
                    <input
                      id="photo-upload-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handlePhotoUpload(file);
                          e.target.value = '';
                        }
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input
                      value={profileForm.first_name}
                      onChange={(e) => setProfileForm({...profileForm, first_name: e.target.value})}
                      placeholder="First name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input
                      value={profileForm.last_name}
                      onChange={(e) => setProfileForm({...profileForm, last_name: e.target.value})}
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    value={user?.email}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    Email cannot be changed. Contact your administrator.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Work Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Input
                    value={user?.department || 'Not assigned'}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    Department is managed by your administrator
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Office Location</Label>
                  <Input
                    value={profileForm.office_location}
                    onChange={(e) => setProfileForm({...profileForm, office_location: e.target.value})}
                    placeholder="e.g., New York Office, Remote"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <Popover open={tzOpen} onOpenChange={setTzOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={tzOpen}
                          className="w-full justify-between font-normal"
                        >
                          {profileForm.timezone || 'Select timezone...'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[320px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search timezone..." />
                          <CommandList>
                            <CommandEmpty>No timezone found.</CommandEmpty>
                            <CommandGroup>
                              {TIMEZONES.map(tz => (
                                <CommandItem
                                  key={tz}
                                  value={tz}
                                  onSelect={(val) => {
                                    setProfileForm({ ...profileForm, timezone: val });
                                    setTzOpen(false);
                                  }}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${profileForm.timezone === tz ? 'opacity-100' : 'opacity-0'}`} />
                                  {tz}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select
                      value={profileForm.language}
                      onValueChange={(value) => setProfileForm({...profileForm, language: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Spanish</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Roles & Permissions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>User Roles</Label>
                  <div className="flex flex-wrap gap-2">
                    {user?.roles?.map((role, idx) => (
                      <Badge key={idx} variant="default">
                        {getRoleLabel(role)}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Roles are managed by your administrator
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 border rounded">
                    <p className="text-sm font-medium mb-1">Financial Access</p>
                    <Badge variant={user?.has_financial_access ? 'default' : 'secondary'}>
                      {user?.has_financial_access ? 'Granted' : 'Not Granted'}
                    </Badge>
                  </div>
                  <div className="p-3 border rounded">
                    <p className="text-sm font-medium mb-1">Sensitive Data Access</p>
                    <Badge variant={user?.has_sensitive_access ? 'default' : 'secondary'}>
                      {user?.has_sensitive_access ? 'Granted' : 'Not Granted'}
                    </Badge>
                  </div>
                </div>

                {user?.is_team_leader && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm font-medium text-blue-900">
                      Team Leader: {user.team_leader_department}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleSaveProfile} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Current Password *</Label>
                  <Input
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                    placeholder="Enter current password"
                  />
                </div>

                <div className="space-y-2">
                  <Label>New Password *</Label>
                  <Input
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                    placeholder="Enter new password"
                  />
                  <p className="text-xs text-muted-foreground">
                    Must be at least 8 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Confirm New Password *</Label>
                  <Input
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                    placeholder="Confirm new password"
                  />
                </div>

                <div className="flex justify-end pt-4">
                  <Button 
                    onClick={handleChangePassword} 
                    disabled={saving}
                    className="bg-primary hover:bg-primary/90 text-white"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        Change Password
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-muted/30">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Security Tips</p>
                    <ul className="text-xs text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                      <li>Use a strong, unique password</li>
                      <li>Don&apos;t share your password with anyone</li>
                      <li>Change your password regularly</li>
                      <li>Avoid using personal information in your password</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <OnboardingRelaunch />
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="alerts" className="space-y-4">

            {/* Push Notifications */}
            <PushNotificationSettings />

            {/* Accounts I Follow — at the top since both FF and Sale alerts use these */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Accounts I Follow</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Alerts for sale opportunities and farm care events will fire for horses owned by these accounts.
                </p>
              </CardHeader>
              <CardContent>
                {!alertPrefs ? (
                  <div className="text-center py-4">
                    <Button variant="outline" onClick={loadAlertPrefs}>Load Preferences</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input className="pl-8 h-8 text-sm" placeholder="Search accounts..."
                        value={alertAccountSearch} onChange={e => setAlertAccountSearch(e.target.value)} />
                    </div>
                    {subscribedCount > 0 && (
                      <p className="text-xs text-muted-foreground">{subscribedCount} account{subscribedCount !== 1 ? 's' : ''} selected</p>
                    )}
                    <div className="max-h-56 overflow-y-auto space-y-1 border rounded p-2">
                      {alertAccounts
                        .filter(a => a.system_name?.toLowerCase().includes(alertAccountSearch.toLowerCase()))
                        .map(acct => {
                          const isOn = (alertPrefs.subscribed_account_ids || []).includes(acct.id);
                          return (
                            <div key={acct.id} onClick={() => toggleAlertAccount(acct.id)}
                              className={`flex items-center justify-between px-3 py-2 rounded cursor-pointer text-sm hover:bg-muted/50 transition-colors ${isOn ? 'bg-green-50 border border-green-200' : ''}`}>
                              <span className="font-medium">{acct.system_name}</span>
                              {isOn ? <Bell className="w-4 h-4 text-primary fill-primary" />
                                    : <Bell className="w-4 h-4 text-muted-foreground" />}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* FarmFolio Alert Preferences — only for FarmFolio tenants */}
            {isFarmComputed && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">FarmFolio Alert Preferences</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Get notified about care events for horses owned by accounts you follow.
                </p>
              </CardHeader>
              <CardContent>
                {!alertPrefs ? (
                  <div className="text-center py-6">
                    <Button variant="outline" onClick={loadAlertPrefs}>Load Preferences</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[
                      { key: 'ff_layup',      label: 'Layup',       desc: 'When a horse is placed on layup' },
                      { key: 'ff_medication', label: 'Medication',   desc: 'When a medication is logged' },
                      { key: 'ff_vaccination',label: 'Vaccination',  desc: 'When a vaccination is logged' },
                      { key: 'ff_procedure',  label: 'Procedure',    desc: 'When a medical procedure is logged' },
                      { key: 'ff_travel',     label: 'Travel',       desc: 'When a travel event is logged' },
                      { key: 'ff_bred',       label: 'Bred',         desc: 'When a cover record is logged' },
                      { key: 'ff_pregnant',   label: 'Pregnant',     desc: 'When a mare is confirmed pregnant' },
                      { key: 'ff_foaled',     label: 'Foaled',       desc: 'When a delivery milestone is recorded' },
                    ].map(({ key, label, desc }) => {
                      const pref = alertPrefs.alert_preferences?.[key] || { enabled: false, methods: ['portal'] };
                      return (
                        <div key={key} className="border rounded p-3">
                          <div className="flex items-center justify-between mb-1.5">
                            <div>
                              <p className="text-sm font-medium">{label}</p>
                              <p className="text-[10px] text-muted-foreground">{desc}</p>
                            </div>
                            <Button variant={pref.enabled ? 'default' : 'outline'} size="sm"
                              className={`h-7 text-xs ${pref.enabled ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
                              onClick={() => toggleAlertType(key, 'enabled')}>
                              {pref.enabled ? 'On' : 'Off'}
                            </Button>
                          </div>
                          {pref.enabled && (
                            <div className="flex gap-2 mt-1">
                              {['portal','email','text'].map(method => (
                                <Button key={method} size="sm"
                                  variant={pref.methods?.includes(method) ? 'default' : 'outline'}
                                  className={`h-6 text-[10px] capitalize ${pref.methods?.includes(method) ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
                                  onClick={() => toggleAlertMethod(key, method)}>
                                  {method === 'portal' ? 'In App' : method === 'email' ? 'Email' : 'Push Notification'}
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Sale Alert Preferences — Alert Types only (Accounts moved to top) */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Sale Alert Preferences</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Get notified about sale opportunity events for accounts you follow.
                </p>
              </CardHeader>
              <CardContent>
                {!alertPrefs ? (
                  <div className="text-center py-6">
                    <Button variant="outline" onClick={loadAlertPrefs}>Load Preferences</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ALERT_TYPES.map(({ key, label, desc }) => {
                      const pref = alertPrefs.alert_preferences?.[key] || { enabled: false, methods: ['portal'] };
                      return (
                        <div key={key} className="border rounded p-3">
                          <div className="flex items-center justify-between mb-1.5">
                            <div>
                              <p className="text-sm font-medium">{label}</p>
                              <p className="text-[10px] text-muted-foreground">{desc}</p>
                            </div>
                            <Button variant={pref.enabled ? 'default' : 'outline'} size="sm"
                              className={`h-7 text-xs ${pref.enabled ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
                              onClick={() => toggleAlertType(key, 'enabled')}>
                              {pref.enabled ? 'On' : 'Off'}
                            </Button>
                          </div>
                          {pref.enabled && (
                            <div className="flex gap-2 mt-1">
                              {['portal','email','text'].map(method => (
                                <Button key={method} size="sm"
                                  variant={pref.methods?.includes(method) ? 'default' : 'outline'}
                                  className={`h-6 text-[10px] capitalize ${pref.methods?.includes(method) ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
                                  onClick={() => toggleAlertMethod(key, method)}>
                                  {method === 'portal' ? 'In App' : method === 'email' ? 'Email' : 'Push Notification'}
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tickets Tab */}
          <TabsContent value="support" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <LifeBuoy className="w-4 h-4 text-primary" /> My Support Tickets
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {ticketsLoading2 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">Loading...</p>
                ) : myTickets.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground">
                    <LifeBuoy className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No support tickets yet.</p>
                    <p className="text-xs mt-1">Use the chat assistant to raise a support request.</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {myTickets.map(t => {
                      const statusColors = {
                        open: 'bg-red-100 text-red-700',
                        in_progress: 'bg-yellow-100 text-yellow-700',
                        resolved: 'bg-green-100 text-green-700',
                        closed: 'bg-gray-100 text-gray-600',
                      };
                      const fmtDate = (d) => d ? fmtDatetime(d, profileForm.timezone || 'America/New_York') : '—';
                      return (
                        <div key={t.id} className="px-4 py-3 hover:bg-muted/20">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{t.summary}</p>
                              <div className="flex items-center gap-3 mt-1">
                                <span className="text-xs font-mono text-muted-foreground">#{t.id?.slice(0, 8).toUpperCase()}</span>
                                <span className="text-xs text-muted-foreground">Opened {fmtDate(t.created_at)}</span>
                                {t.updated_at !== t.created_at && (
                                  <span className="text-xs text-muted-foreground">Updated {fmtDate(t.updated_at)}</span>
                                )}
                                <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[t.status] || 'bg-gray-100 text-gray-600'}`}>
                                  {t.status?.replace('_', ' ') || 'open'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </SidebarLayout>
  );
};

export default UserProfilePage;
